import { Component, computed, input, OnInit, output, signal, viewChild } from '@angular/core';
import { delay } from '@gametailors/web-core/misc';
import { RidAssessmentToast } from '../../components/rid-assessment-toast/rid-assessment-toast';
import { RidAudioButton, TAudioSource } from '../../components/rid-audio-button/rid-audio-button';
import { RidBottomButton } from '../../components/rid-bottom-button/rid-bottom-button';
import { RidKlankvoetRuleModal, TKlankvoetRule } from '../../components/rid-klankvoet-rule-modal/rid-klankvoet-rule-modal';
import { RidMechanicBackground } from '../../components/rid-mechanic-background/rid-mechanic-background';
import { RidMechanicContent } from '../../components/rid-mechanic-content/rid-mechanic-content';
import { RidMechanicTitle } from '../../components/rid-mechanic-title/rid-mechanic-title';
import { RidMechanicWrapper } from '../../components/rid-mechanic-wrapper/rid-mechanic-wrapper';
import { RidModalBackground } from '../../components/rid-modal-background/rid-modal-background';
import { RidOverlayFeedback } from '../../components/rid-overlay-feedback/rid-overlay-feedback';
import { RidTextInput } from '../../components/rid-text-input/rid-text-input';
import { RidTopBar } from '../../components/rid-top-bar/rid-top-bar';
import { RidSfxDirective } from '../../directives/rid-sfx.directive';
import { IconKlankvoetNormal } from '../../icons/i-klankvoet-normal';
import { IconKlankvoetStom } from '../../icons/i-klankvoet-stom';
import { AudioEffectPlayer } from '../../utils/audio.utils';

export type TKlankvoet = '>' | '<';

export interface TKlankvoetenAssignment {
  word: string;
  audio: string | { pronounce: string };
  /**
   * The word broken up per klankvoet, e.g. `[{ part: 'tij', type: '>' }, { part: 'ger', type: '<' }]` for 'tijger'. `part` is the text segment that gets pronounced, `type` is the klankvoet the user needs to pick for it.
   */
  klankvoeten: { part: string; type: TKlankvoet }[];
  rule: TKlankvoetRule;
}

const TITLE_TRANSITION_DELAY = 500;

@Component({
  selector: 'rid-klankvoeten',
  imports: [
    RidMechanicBackground,
    RidMechanicWrapper,
    RidTopBar,
    RidMechanicContent,
    RidMechanicTitle,
    RidAudioButton,
    RidAssessmentToast,
    RidBottomButton,
    RidOverlayFeedback,
    RidModalBackground,
    RidKlankvoetRuleModal,
    RidTextInput,
    IconKlankvoetNormal,
    IconKlankvoetStom,
    RidSfxDirective,
  ],
  templateUrl: './rid-klankvoeten.html',
  host: {
    class: 'block',
  },
})
export class RidKlankvoeten implements OnInit {
  assignments = input.required<TKlankvoetenAssignment[]>();

  index = signal(0);
  assignment = computed(() => this.assignments()[this.index()]);
  // Once the answer has been revealed, the audio button replays the word split up per klankvoet instead of the prompt.
  audioSrc = computed<TAudioSource | TAudioSource[]>(() =>
    this.phase() === 'SPEAK' ? this.assignment().audio : this.assignment().klankvoeten.map(({ part }) => ({ pronounce: part })),
  );

  // Phase progression within a single word: SPEAK (say it out loud) -> ASSESS (self-check) -> SPLIT (tag the klankvoeten).
  phase = signal<'SPEAK' | 'ASSESS' | 'SPLIT'>('SPEAK');
  // Which instruction title is showing, kept separate from `phase` so we can hide the old one, let
  // its leave animation finish, and only then show the next - instead of both overlapping in place.
  titleKey = signal<'SPEAK' | 'ASSESS' | undefined>(undefined);
  // Revealed once the first pronunciation finishes, letting the user decide when to see the assessment buttons.
  canReveal = signal(false);
  // The ASSESS toast only appears once the answer has been pronounced (part by part), not the instant the phase flips.
  showAnswer = signal(false);
  showExplanation = signal(false);
  isEvaluating = signal(false);

  splitIndex = signal(0);
  splitAnswers = signal<(TKlankvoet | undefined)[]>([]);
  isWordComplete = computed(() => this.splitIndex() >= this.assignment().klankvoeten.length);
  // Index of the klankvoet whose part is currently being pronounced, so it can be highlighted.
  highlightIndex = signal<number | undefined>(undefined);

  audioButton = viewChild<RidAudioButton>('audioButton');
  overlayFeedback = viewChild.required<RidOverlayFeedback>('overlayFeedback');

  finished = output<void>();

  ngOnInit(): void {
    this.resetWordState();
    this.titleKey.set('SPEAK');
  }

  private resetWordState() {
    this.phase.set('SPEAK');
    this.canReveal.set(false);
    this.showAnswer.set(false);
    this.isEvaluating.set(false);
    this.splitIndex.set(0);
    this.splitAnswers.set(this.assignment().klankvoeten.map(() => undefined));
    this.highlightIndex.set(undefined);
  }

  // Triggered once the (persistent) audio button pops in for a new word.
  async speak() {
    await this.audioButton()?.play();
    this.canReveal.set(true);
  }

  async reveal() {
    this.canReveal.set(false);
    this.titleKey.set(undefined);
    await delay(TITLE_TRANSITION_DELAY);

    this.phase.set('ASSESS');
    this.titleKey.set('ASSESS');
    AudioEffectPlayer.play('HINT');
    await delay(400);

    // Now that we're past SPEAK, the audio button plays each klankvoet separately rather than the prompt's whole-word audio, so the split itself is audible (e.g. 'tijger' -> 'tij' … 'ger').
    await this.audioButton()?.play();
    await delay(400);

    this.showAnswer.set(true);
  }

  async assess(result: 'CORRECT' | 'INCORRECT') {
    this.showAnswer.set(false);

    if (result === 'INCORRECT') {
      AudioEffectPlayer.play('INCORRECT');
      this.overlayFeedback().showIncorrect();
      // Let the red flash register before the explanation covers it.
      await delay(400);
      this.showExplanation.set(true);
    } else {
      AudioEffectPlayer.play('CORRECT');
      this.overlayFeedback().showCorrect();
      this.titleKey.set(undefined);
      this.phase.set('SPLIT');
    }
  }

  closeExplanation() {
    this.showExplanation.set(false);
    this.next();
  }

  selectKlankvoet(value: TKlankvoet) {
    if (this.isEvaluating() || this.isWordComplete()) return;

    const expected = this.assignment().klankvoeten[this.splitIndex()].type;
    if (value !== expected) {
      AudioEffectPlayer.play('INCORRECT');
      this.overlayFeedback().showIncorrect();
      return;
    }

    this.splitAnswers.update((answers) => {
      const next = [...answers];
      next[this.splitIndex()] = value;
      return next;
    });

    const isComplete = this.splitIndex() + 1 >= this.assignment().klankvoeten.length;
    if (isComplete) {
      this.markComplete();
    } else {
      this.splitIndex.update((i) => i + 1);
    }
  }

  async markComplete() {
    this.isEvaluating.set(true);

    AudioEffectPlayer.play('CORRECT');
    this.overlayFeedback().showCorrect();
    await delay(400);

    await this.audioButton()?.play((i) => this.highlightIndex.set(i));
    await delay(150);
    this.highlightIndex.set(undefined);

    await delay(250);
    this.next();
  }

  next() {
    const isLast = this.index() === this.assignments().length - 1;

    if (isLast) {
      this.finish();
      return;
    }

    // The whole per-word stage below is keyed to the assignment, so bumping index here fades it out as-is (whatever phase it was in) and pops the next word's stage in fresh, a beat later.
    this.index.update((i) => i + 1);
    this.resetWordState();
    this.titleKey.set(undefined);
    void this.revealSpeakTitle();
  }

  private async revealSpeakTitle() {
    await delay(TITLE_TRANSITION_DELAY);
    this.titleKey.set('SPEAK');
  }

  finish() {
    this.finished.emit();
  }
}
